/**
 * Whose hives am I looking at (#286).
 *
 * WHY THIS EXISTS
 * ---------------
 * Reported after testing, and it only appears once you are on somebody
 * else's team: the dashboard and hive list mix your own hives with hives
 * from every account you help at, with nothing saying which is which.
 * The reporter helps more than one small owner, so "mine versus theirs"
 * is not even a single distinction -- it is one per account.
 *
 * WHY GROUPS AND NOT A COLUMN
 * ---------------------------
 * A column showing the owner works at two accounts and stops working at
 * four, because the reader ends up scanning it to reconstruct groups the
 * app could have made for them.
 *
 * WHAT DOES NOT CHANGE
 * --------------------
 * The ranking in #282 stays GLOBAL. An urgent issue at an account you
 * help at still outranks a routine check at your own, and burying it in
 * a second section would defeat the panel that exists to surface it.
 * Sections are for BROWSING; "needs you now" is for deciding.
 */

/** Hives belonging to nobody identifiable, grouped last rather than lost. */
const UNKNOWN_ACCOUNT = "__unknown__";

/**
 * Split hives into "mine" and one group per other account.
 *
 * Your own account comes first and is never labelled with a name -- "My
 * hives" is what a person calls them, and seeing your own apiary name in
 * a heading on your own dashboard is noise.
 */
export function groupByAccount(hives) {
  const mine = [];
  const others = new Map();

  for (const hive of hives || []) {
    const account = hive.account_user_id;
    // is_my_account is decided by the SERVER, which already knows who is
    // asking. Shipping the caller's user_id to the browser so it can
    // compare would mean a client that has to be told who it is in order
    // to read a list -- and a client that can be told wrong.
    if (hive.is_my_account || account == null) {
      // A row with no account at all is an older response, or a hive the
      // API could not attribute. It stays with yours rather than getting
      // a section of its own: a hive you can see is far likelier to be
      // yours than a stranger's.
      mine.push(hive);
      continue;
    }
    const key = String(account);
    if (!others.has(key)) {
      others.set(key, {
        account_user_id: account,
        // The label may be missing where the name could not be resolved.
        // "Another apiary" is honest; a blank heading is not.
        label: hive.account_label || "Another apiary",
        hives: [],
      });
    }
    others.get(key).hives.push(hive);
  }

  const groups = [];
  if (mine.length) {
    groups.push({ account_user_id: null, label: "My hives", mine: true, hives: mine });
  }
  // Alphabetical by name, so the order does not shuffle as hives are
  // added. Ordering by "most hives" or "most urgent" would move a whole
  // section under somebody mid-scroll.
  groups.push(
    ...[...others.values()]
      .map((g) => ({ ...g, mine: false }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" })),
  );
  return groups;
}

/**
 * True when the grouping is worth SHOWING.
 *
 * One account means one heading over the whole list, which is a heading
 * that answers a question nobody asked. The vast majority of beekeepers
 * are on their own, and they should see exactly what they saw before.
 */
export function hasMultipleAccounts(hives) {
  return groupByAccount(hives).length > 1;
}

export { UNKNOWN_ACCOUNT };
